import { AdapterRegistry } from 'app/model/adapters/adapter-registry';
import { Model } from 'app/model/backendmodel';
import { IModel } from 'shell-models-lib';
import { Metamodel } from 'app/model/base/model';
import { LinkedServiceAdapter } from 'app/model/adapters/linkedservices/linkedservice.adapter';
import { parameterizablePasswordOrAKVtoSecretBase, parameterizableSecretBaseToModel } from 'app/model/adapters/linkedservices/azure-key-vault.util';
import { stringIsNullOrEmpty } from 'app/shared/utils/string.util';
import { SapOdpLinkedService } from 'app/model/linkedservice/sap-odp.linkedservice';
import { ValueAdapter } from 'app/model/adapters/value.adapter';

@AdapterRegistry.adapter({
    model: Metamodel.modelFor(SapOdpLinkedService),
    asynchronous: true,
    adapts: {
        name: '*',
        properties: {
            description: '*',
            type: '*',
            connectVia: '*',
            typeProperties: {
                server: '*',
                systemNumber: '*',
                messageServer: '*',
                messageServerService: '*',
                systemId: '*',
                logonGroup: '*',
                clientId: '*',
                language: '*',
                sncMode: '*',
                sncMyName: '*',
                sncPartnerName: '*',
                sncLibraryPath: '*',
                sncQop: '*',
                userName: '*',
                x509CertificatePath: '*',
                subscriberName: '*',
                password: 'secret',
                encryptedCredential: '*',
                isSSO: '*'
            },
            parameter: '*'
        }
    }
})
export class SapOdpLinkedServiceAdapter extends LinkedServiceAdapter<SapOdpLinkedService> {
    protected supportFirstClassParameterExperience = true;

    protected modelConstructor(): new () => SapOdpLinkedService {
        return SapOdpLinkedService;
    }

    protected model(): IModel {
        return SapOdpLinkedService.MODEL;
    }

    protected async propertiesToResourceAsync(linkedService: SapOdpLinkedService, linkedServiceResource: Model.LinkedServiceResource): Promise<Model.LinkedServiceResource> {
        const properties = this.properties(linkedServiceResource) as Model.SapOdpLinkedService;

        properties.type = this.model().name;
        properties.typeProperties = {
            clientId: this.valueToResource(linkedService, SapOdpLinkedService.CLIENT_ID) || undefined,
            language: this.valueToResource(linkedService, SapOdpLinkedService.LANGUAGE) || undefined,
            sncMode: linkedService.sncMode,
            userName: this.valueToResource(linkedService, SapOdpLinkedService.USER_NAME) || undefined,
            subscriberName: this.valueToResource(linkedService, SapOdpLinkedService.SUBSCRIBER_NAME) || undefined,
            password: parameterizablePasswordOrAKVtoSecretBase(linkedService, SapOdpLinkedService.PASSWORD, this.context)
        };
        if (linkedService.logonType === 'applicationServer') {
            properties.typeProperties.server = this.valueToResource(linkedService, SapOdpLinkedService.SERVER) || undefined;
            properties.typeProperties.systemNumber = this.valueToResource(linkedService, SapOdpLinkedService.SYSTEM_NUMBER) || undefined;
        } else {
            properties.typeProperties.messageServer = this.valueToResource(linkedService, SapOdpLinkedService.MESSAGE_SERVER) || undefined;
            properties.typeProperties.messageServerService = this.valueToResource(linkedService, SapOdpLinkedService.MESSAGE_SERVER_SERVICE) || undefined;
            properties.typeProperties.systemId = this.valueToResource(linkedService, SapOdpLinkedService.SYSTEM_ID) || undefined;
            properties.typeProperties.logonGroup = this.valueToResource(linkedService, SapOdpLinkedService.LOGON_GROUP) || undefined;
        }

        if (linkedService.sncMode) {
            properties.typeProperties.sncMyName = this.valueToResource(linkedService, SapOdpLinkedService.SNC_MY_NAME) || undefined;
            properties.typeProperties.sncPartnerName = this.valueToResource(linkedService, SapOdpLinkedService.SNC_PARTNER_NAME) || undefined;
            properties.typeProperties.sncLibraryPath = this.valueToResource(linkedService, SapOdpLinkedService.SNC_LIBRARY_PATH) || undefined;
            properties.typeProperties.sncQop = this.valueToResource(linkedService, SapOdpLinkedService.SNC_QOP) || undefined;
            properties.typeProperties.x509CertificatePath = this.valueToResource(linkedService, SapOdpLinkedService.X509_CERTIFICATE_PATH) || undefined;
            properties.typeProperties.isSSO = this.valueToResource(linkedService, SapOdpLinkedService.IS_SSO);
        }

        return linkedServiceResource;
    }

    protected async propertiesToModelAsync(linkedServiceResource: Model.LinkedServiceResource, linkedService: SapOdpLinkedService): Promise<SapOdpLinkedService> {
        const properties = linkedServiceResource.properties as Model.SapOdpLinkedService;
        const typeProperties = properties.typeProperties;
        if (typeProperties != null) {
            linkedService.logonType = !stringIsNullOrEmpty(typeProperties.messageServer) ? 'group' : 'applicationServer';
            if (linkedService.logonType === 'applicationServer') {
                ValueAdapter.toModel(linkedService, SapOdpLinkedService.SERVER, typeProperties.server);
                ValueAdapter.toModel(linkedService, SapOdpLinkedService.SYSTEM_NUMBER, typeProperties.systemNumber);
            } else {
                ValueAdapter.toModel(linkedService, SapOdpLinkedService.MESSAGE_SERVER, typeProperties.messageServer);
                ValueAdapter.toModel(linkedService, SapOdpLinkedService.MESSAGE_SERVER_SERVICE, typeProperties.messageServerService);
                ValueAdapter.toModel(linkedService, SapOdpLinkedService.SYSTEM_ID, typeProperties.systemId);
                ValueAdapter.toModel(linkedService, SapOdpLinkedService.LOGON_GROUP, typeProperties.logonGroup);
            }

            ValueAdapter.toModel(linkedService, SapOdpLinkedService.CLIENT_ID, typeProperties.clientId);
            ValueAdapter.toModel(linkedService, SapOdpLinkedService.LANGUAGE, typeProperties.language);

            linkedService.sncMode = typeProperties.sncMode != null ? typeProperties.sncMode : false;
            if (linkedService.sncMode) {
                ValueAdapter.toModel(linkedService, SapOdpLinkedService.SNC_MY_NAME, typeProperties.sncMyName);
                ValueAdapter.toModel(linkedService, SapOdpLinkedService.SNC_PARTNER_NAME, typeProperties.sncPartnerName);
                ValueAdapter.toModel(linkedService, SapOdpLinkedService.SNC_LIBRARY_PATH, typeProperties.sncLibraryPath);
                ValueAdapter.toModel(linkedService, SapOdpLinkedService.SNC_QOP, typeProperties.sncQop);
                ValueAdapter.toModel(linkedService, SapOdpLinkedService.X509_CERTIFICATE_PATH, typeProperties.x509CertificatePath);
                ValueAdapter.toModel(linkedService, SapOdpLinkedService.IS_SSO, typeProperties.isSSO);
            }

            ValueAdapter.toModel(linkedService, SapOdpLinkedService.SUBSCRIBER_NAME, typeProperties.subscriberName);
            ValueAdapter.toModel(linkedService, SapOdpLinkedService.USER_NAME, typeProperties.userName);
            parameterizableSecretBaseToModel(linkedService, SapOdpLinkedService.PASSWORD, typeProperties.password, this.context);
        }
        return linkedService;
    }
}
